// =============================================================================
// components/ui/Avatar.tsx — round avatar with initial fallback.
//
// BARE-RN: expo-image Image → react-native Image (blueprint §1 swap #4, de-risk
// to the RN built-in; no contentFit/transition — resizeMode='cover' is the
// equivalent). Behaviour is otherwise identical to the Expo version.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import CachedImage from './CachedImage';
import { colors, fonts } from '../../constants/theme';
import { displayableIcon } from '../../lib/icons';

type AvatarSize = 24 | 32 | 40 | 48 | 56;

interface AvatarProps {
  uri?: string | null;
  name?: string;
  size?: AvatarSize;
  online?: boolean;
}

const BG_COLORS = ['#6C5CE7', '#A855F7', '#3B82F6', '#F59E0B', '#2DB55D', '#FF4757'];

function getColor(name: string): string {
  const safe = name || '?';
  let hash = 0;
  for (let i = 0; i < safe.length; i++) {
    hash = safe.charCodeAt(i) + ((hash << 5) - hash);
  }
  return BG_COLORS[Math.abs(hash) % BG_COLORS.length] || BG_COLORS[0];
}

export default function Avatar({
  uri,
  name = '?',
  size = 40,
  online,
}: AvatarProps) {
  const safeName = (name || '?').trim() || '?';
  const letter = (safeName.charAt(0) || '?').toUpperCase();
  const fontSize = size * 0.4;
  const dotSize = Math.max(8, size * 0.22);
  const validUri = displayableIcon(typeof uri === 'string' ? uri.trim() : undefined) || null;

  return (
    <View
      style={[styles.container, { width: size, height: size }]}
      accessible
      accessibilityRole="image"
      accessibilityLabel={`${safeName}'s avatar${online ? ', online' : ''}`}
    >
      {validUri ? (
        <CachedImage
          source={{ uri: validUri }}
          style={[
            styles.image,
            { width: size, height: size, borderRadius: size / 2 },
          ]}
          resizeMode="cover"
        />
      ) : (
        <View
          style={[
            styles.fallback,
            {
              width: size,
              height: size,
              borderRadius: size / 2,
              backgroundColor: getColor(safeName),
            },
          ]}
        >
          <Text style={[styles.letter, { fontSize }]}>{letter}</Text>
        </View>
      )}
      {online && (
        <View
          style={[
            styles.dot,
            {
              width: dotSize,
              height: dotSize,
              borderRadius: dotSize / 2,
              borderWidth: dotSize > 10 ? 2 : 1.5,
            },
          ]}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  image: {
    backgroundColor: colors.faint,
  },
  fallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  letter: {
    color: colors.white,
    ...fonts.bold,
  },
  dot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: colors.success,
    borderColor: colors.white,
  },
});
